import React, { Children, useContext } from 'react';
import _merge from 'lodash/merge';
import { HorizontalMoreButtonItemIconContext } from 'components/HorizontalMoreButton';
import { getContext, useCombinedRefs } from 'common';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import RippleEffect, { RippleEffectProps } from '../RippleEffect';
import ButtonCore from '../ButtonCore';
import ButtonIcon from '../Button/ButtonIcon';

import Styled from './styles';

export const VARIANT: any = {
  secondary: 'secondary',
  default: 'DEFAULT',
  callToAction: 'call-to-action'
};
const { RADIUS } = Tokens;

const SMALL_ICON_SIZE = '1.25rem';
const MEDIUM_ICON_SIZE = '2rem';

const RIPPLE_STYLE = {
  borderRadius: RADIUS.RADIUS_CIRCLE
};

export interface IconButtonProps extends React.ComponentPropsWithRef<'button'> {
  /** To configure the size of the `IconButton`. */
  size?: 'small' | 'medium';
  /** The variant of the IconButton. "secondary" is identical with "default", please use "secondary" instead of "default" as "default" will be deprecated soon. */
  variant?: 'call-to-action' | 'DEFAULT' | 'secondary';
  /** To render the icon of the button consider using `Icon`. */
  children: React.ReactNode;
  /** If `true`, the button will be disabled. */
  disabled?: boolean;
  /** If `true`, the button will be rendered without ripple effect animation. */
  disabledRipple?: boolean;
  /** Ripple style. (The outermost element is RippleEffect.) */
  rippleStyle?: React.CSSProperties;
  /** To alter the ripple effect DOM element (the outermost element) attributes. */
  rippleProps?: Partial<Omit<RippleEffectProps, 'ref'>>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore internal used by ToggleButton */
  changeable?: boolean;
}

/**
 * Any other props than what documented below (e.g., `onClick`) will be provided to the button element.
 *
 * To alter the outermost element attributes use `rippleProps` prop.
 *
 * To get the ref of the button element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * */
const IconButton = React.forwardRef<HTMLElement, IconButtonProps>((props, ref) =>
  getContext(({ theme }) => {
    const {
      children,
      disabled,
      rippleStyle,
      disabledRipple,
      changeable,
      variant,
      theme: themeProp,
      size,
      rippleProps,
      ...otherProps
    } = props;
    const resolvedTheme = _merge(
      {},
      variant === VARIANT.callToAction ? theme.ICON_BUTTON.CTA : theme.ICON_BUTTON.FLAT,
      themeProp,
      variant === VARIANT.callToAction ? themeProp.CTA : themeProp.FLAT,
    );
    const { size: inheritedSize } = useContext(HorizontalMoreButtonItemIconContext);
    const resolvedSize = inheritedSize || size;
    const isSmall = resolvedSize === 'small';

    const iconButtonRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, iconButtonRef);
    useDidUpdateEffect(() => {
      changeable && combinedRef.current && combinedRef.current.focus();
    }, [variant]);
    const rippleEffectStyle = isSmall ? {
      minWidth: SMALL_ICON_SIZE,
      ...RIPPLE_STYLE,
      ...rippleStyle
    } : {
      minWidth: MEDIUM_ICON_SIZE,
      ...RIPPLE_STYLE,
      ...rippleStyle
    };
    return (
      <RippleEffect
        center
        disabled={disabledRipple || disabled}
        style={rippleEffectStyle}
        rippleStyle={{
          backgroundColor: resolvedTheme.rippleColor
        }}
        {...rippleProps}
      >
        <ButtonCore
          $size={resolvedSize}
          ref={combinedRef}
          iconButton
          $theme={resolvedTheme}
          theme={resolvedTheme}
          Wrapper={Styled.IconButton}
          disabled={disabled}
          {...otherProps}
        >
          {isSmall
            ? React.cloneElement(Children.toArray(children)[0] as React.ReactElement<any>, { size: SMALL_ICON_SIZE })
            : children}
        </ButtonCore>
      </RippleEffect>
    );
  }));

IconButton.displayName = 'IconButton';

IconButton.defaultProps = {
  size: 'medium',
  variant: 'secondary',
  disabled: false,
  disabledRipple: false,
  theme: {},
  changeable: false,
  rippleProps: {},
  rippleStyle: {}
};

export default IconButton;

export {
  ButtonIcon
};
